import { parseAspectRatio, type AspectRatio } from '@/lib/stage'
import type { MediaFit } from '@/types'

/** Where `playlistName` came from — shown in the debug overlay. */
export type PlaylistSource = 'path' | 'query' | 'default'

export interface RuntimeConfig {
  /** Which playlist manifest to load. */
  playlistName: string
  playlistSource: PlaylistSource
  /** Base URL for media files and manifests. */
  mediaBase: string
  /** Shape of the physical screen; `null` fills whatever viewport it gets. */
  aspect: AspectRatio | null
  debug: boolean
  /** Derive the current item from the wall clock so screens stay in step. */
  sync: boolean
  /** Screens are muted by default; audio needs a user gesture in most browsers. */
  sound: boolean
  fitOverride: MediaFit | null
  transitionOverrideMs: number | null
  playlistPollMs: number
  versionPollMs: number
  prepareTimeoutMs: number
  firstFrameTimeoutMs: number
  stallTimeoutMs: number
  healthIntervalMs: number
  /** Local time (`HH:MM`) for the nightly refresh, or `null` to disable it. */
  nightlyReloadAt: string | null
}

const DEFAULTS = {
  playlistName: 'default',
  aspect: '9:16',
  playlistPollMs: 60_000,
  versionPollMs: 300_000,
  prepareTimeoutMs: 20_000,
  firstFrameTimeoutMs: 600,
  stallTimeoutMs: 6_000,
  healthIntervalMs: 2_000,
  nightlyReloadAt: '04:15',
} as const

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/

function readBool(params: URLSearchParams, key: string, fallback: boolean): boolean {
  const raw = params.get(key)
  if (raw === null) return fallback
  if (raw === '' || /^(1|true|yes|on)$/i.test(raw)) return true
  if (/^(0|false|no|off)$/i.test(raw)) return false
  return fallback
}

function readInt(
  params: URLSearchParams,
  key: string,
  fallback: number,
  min: number,
  max: number,
): number {
  const raw = params.get(key)
  if (raw === null) return fallback
  const parsed = Number.parseInt(raw, 10)
  if (!Number.isFinite(parsed)) return fallback
  return Math.min(max, Math.max(min, parsed))
}

function readFit(params: URLSearchParams): MediaFit | null {
  const raw = params.get('fit')
  return raw === 'cover' || raw === 'contain' ? raw : null
}

function readNightly(params: URLSearchParams): string | null {
  const raw = params.get('nightly')
  if (raw === null) return DEFAULTS.nightlyReloadAt
  if (/^(0|false|no|off)$/i.test(raw)) return null
  return TIME_PATTERN.test(raw) ? raw : DEFAULTS.nightlyReloadAt
}

function readAspect(params: URLSearchParams, fallback: string): AspectRatio | null {
  const raw = params.get('aspect')
  if (raw === null) return parseAspectRatio(fallback)

  const parsed = parseAspectRatio(raw)
  if (parsed !== null) return parsed
  // `fill` is a deliberate choice; anything unparseable falls back to the default.
  return /^(fill|stretch|none|off|0)$/i.test(raw.trim()) ? null : parseAspectRatio(fallback)
}

/**
 * Playlist names are lower-cased because `buildMedia` requires the source folders
 * to be lowercase slugs, so a hand-typed `/Kjeller-Venstre` still finds its content.
 */
function normalizeName(raw: string | null): string | null {
  if (raw === null) return null
  const trimmed = raw.trim().toLowerCase()
  // Guards against a stray URL value turning into a path traversal on fetch.
  return /^[a-z0-9][a-z0-9._-]{0,63}$/.test(trimmed) ? trimmed : null
}

/** Real paths on the server, which must never be read as a playlist name. */
const RESERVED_SEGMENTS = new Set(['assets', 'media', 'index.html', 'version.json', 'favicon.ico'])

/**
 * A screen is addressed by path — `/kjeller-venstre` plays that playlist — so the
 * URL in Playipp reads like a place rather than a query string. Only the first
 * segment counts; anything deeper is ignored.
 */
function readPathName(pathname: string): string | null {
  const first = pathname.split('/').find((segment) => segment !== '')
  if (first === undefined) return null

  let decoded = first
  try {
    decoded = decodeURIComponent(first)
  } catch {
    return null // Malformed percent-encoding; treat as no playlist.
  }

  if (RESERVED_SEGMENTS.has(decoded.toLowerCase())) return null
  return normalizeName(decoded)
}

export interface RuntimeDefaults {
  /** Usually `import.meta.env.VITE_MEDIA_BASE`. */
  mediaBase?: string
  /** Usually the screen aspect from `infoscreens.config.js`, e.g. `9:16`. */
  aspect?: string
}

/**
 * All runtime behaviour is driven by the URL so a screen can be re-purposed by
 * editing its address in Playipp, without a rebuild or a deploy.
 *
 * Accepts a full or relative URL — normally `window.location.href`.
 */
export function readRuntimeConfig(href: string, defaults: RuntimeDefaults = {}): RuntimeConfig {
  const url = new URL(href, 'http://screen.invalid')
  const params = url.searchParams
  const mediaBase = (params.get('media') ?? defaults.mediaBase ?? '/media').replace(/\/+$/, '')

  // An explicit query parameter wins, so any screen can be pointed elsewhere for
  // a test without touching its configured address.
  const queryName = normalizeName(params.get('playlist')) ?? normalizeName(params.get('screen'))
  const pathName = readPathName(url.pathname)

  return {
    playlistName: queryName ?? pathName ?? DEFAULTS.playlistName,
    playlistSource: queryName !== null ? 'query' : pathName !== null ? 'path' : 'default',
    mediaBase,
    aspect: readAspect(params, defaults.aspect ?? DEFAULTS.aspect),
    debug: readBool(params, 'debug', false),
    sync: readBool(params, 'sync', true),
    sound: readBool(params, 'sound', false),
    fitOverride: readFit(params),
    transitionOverrideMs:
      params.get('transition') === null ? null : readInt(params, 'transition', 600, 0, 5_000),
    playlistPollMs: readInt(params, 'poll', DEFAULTS.playlistPollMs, 5_000, 3_600_000),
    versionPollMs: readInt(params, 'versionPoll', DEFAULTS.versionPollMs, 30_000, 86_400_000),
    prepareTimeoutMs: readInt(params, 'prepareTimeout', DEFAULTS.prepareTimeoutMs, 2_000, 120_000),
    firstFrameTimeoutMs: readInt(params, 'frameTimeout', DEFAULTS.firstFrameTimeoutMs, 0, 5_000),
    stallTimeoutMs: readInt(params, 'stallTimeout', DEFAULTS.stallTimeoutMs, 2_000, 120_000),
    healthIntervalMs: readInt(params, 'healthInterval', DEFAULTS.healthIntervalMs, 500, 60_000),
    nightlyReloadAt: readNightly(params),
  }
}
